/**
 * Helper: General
 * -----------------------------------------------------------------------------
 * General helper utility functions.
 * - Functions which don't fit into any of the other utils files.
 * - Add in alphabetical order.
 *
 */

/**
 * Capitalizes a given string.
 * @param {String} value - The string to update.
 * @returns {String}
 */
export function capitalize(value) {
  const normalisedStr = value.trim().toLowerCase()

  return normalisedStr.split(' ').map((word) =>
    word.charAt(0).toUpperCase() + word.slice(1),
  ).join(' ')
}

/**
 * Debounce functions for better performance
 * (c) 2018 Chris Ferdinandi, MIT License, https://gomakethings.com
 * @param {Function} callback The function to debounce.
 * @param {Number} wait - The amount of time before debounce call is triggered.
 * @param {Boolean} immediate - Trigger the immediately.
 */
export function debounce(callback, wait, immediate) {
  let timeout = null

  return function(...args) {
    const callNow = immediate && !timeout

    function later() {
      timeout = null
      if (!immediate) {
        callback.apply(this, args)
      }
    }

    window.clearTimeout(timeout)
    timeout = window.setTimeout(later, wait)

    if (callNow) {
      callback.apply(this, args)
    }
  }
}

/**
 * Generate an ID for instances where you need a unique namespace such as
 * overlays or carousel controls.
 * @returns {string}
 */
export function generateId() {
  return Math.random().toString(16).slice(2)
}

/**
 * Get an icon image associated with the Liquid link object.
 * @param {Object} link - The Liquid link tier in the navigation.
 * @param {Object} link.options - Shortcode options from /admin/menus
 * settings.
 * @param {String} link.options.image - The icon url.
 * @param {String} link.options.image_hover - The icon url for hover states.
 * @param {Boolean} hover - Get the image assigned for hover states.
 * @returns {string} - The icon src url.
 */
export function getIcon(link, hover = false) {
  if (!link.options?.image) {
    return ''
  }

  return hover === true ?
    (link.options.image_hover || link.options.image) :
    link.options.image
}

/**
 * Generate a CSS translate string.
 * @param {Number} value - The value to translate by.
 * @param {String} unit - The CSS unit to translate by.
 * @returns {string}
 */
export function getTranslateString(value, unit = '%') {
  return `translateX(${value}${unit})`
}

/**
 * Get the translate value as a percent.
 * @param {HTMLElement} element - The HTML element that was translated.
 * @returns {number} - The percent value without '%'.
 */
export function getTranslateX(element) {
  const styles = window.getComputedStyle(element)
  const width = styles.width
  const matrix = new window.WebKitCSSMatrix(styles.transform)
  return matrix.m41 / parseInt(width) * 100
}

/**
 * Handle mouseenter and mouseleave icon states for navigation items.
 * @param {Object} event - mouseenter or mouseleave events.
 * @param {Object} link - The link tier object from Liquid.
 * @param {HTMLElement} icon - The <img> element.
 */
export function handleIconHover(event, link, icon) {
  if (!icon) {
    return
  }

  if (event.type === 'mouseenter' || event.type === 'touchstart') {
    icon.src = getIcon(link, true)
  }
  else {
    icon.src = getIcon(link)
  }
}

/**
 * Detects if a part of an element is in the viewport.
 * @param {HTMLElement} element - Element to check.
 * @param {Number} visibilityOffset - Additional threshold to being visible
 * within window
 * @returns {Boolean} - True if element is at least partially in viewport.
 */
export function isVisible(element, visibilityOffset = 0) {
  if (!element || !(element instanceof HTMLElement)) {
    return false
  }
  const windowHeight = window.innerHeight - visibilityOffset

  const elementRectangle = element.getBoundingClientRect()
  const elementTop = elementRectangle.top
  const elementBottom = elementRectangle.bottom

  const topInView = (elementTop > 0) && (elementTop < windowHeight)
  const bottomInView = (elementBottom > 0) && (elementBottom < windowHeight)

  return (topInView || bottomInView)
}

/**
 * Scrolls page smoothly to element.
 * @param {HTMLElement} element - Element to scroll to.
 * @param {Object} data - Additional data.
 * @param {Function} data.callback - Function run after completion.
 * @param {HTMLElement} data.page - Element to scroll.
 */
export function scrollToElement(element, { callback, page = window } = {}) {
  let viewportFromPageTop = window.pageYOffset

  if (page !== window) {
    viewportFromPageTop = 0
  }

  const elementRectangle = element.getBoundingClientRect()
  const elementFromViewportTop = elementRectangle.top
  const position = (viewportFromPageTop + elementFromViewportTop)

  /**
   * Disable smooth scroll if prefers reduced motion.
   */
  const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ? 'auto'
    : 'smooth'

  page.scroll({
    behavior,
    left: 0,
    top: position,
  })

  if (typeof callback === 'function') {
    callback()
  }
}

/**
 * Set intersection observers to detect when add to cart button leaves viewport.
 * @param {Object} data - Data object.
 * @param {Object} data.callback - Function to run when intersecting.
 * @param {HTMLElement} data.element - Element to set observer on.
 * unobserved, must be a Vue element.
 * @param {String} [data.rootMargin] - This set of values serves to grow or shrink
 * each side of the root element's bounding box before computing intersections.
 * @param {Number|Array} [data.threshold] - Intersection observer threshold, 0.5
 * means the intersection is triggered when 50% of the element is in view.
 * @returns {Function}
 */
export function setIntersectionObserver({
  callback,
  element,
  rootMargin = '0px',
  threshold = 0.5,
}) {
  const options = {
    root: null,
    rootMargin,
    threshold,
  }

  const observer = new IntersectionObserver(callback, options)
  observer.observe(element)
}

/**
 * Returns a function, that, as long as it continues to be invoked,
 * will not be triggered. The function will be called after it stops
 * being called for N milliseconds. If `immediate` is passed, trigger
 * the function on the leading edge, instead of the trailing.
 *
 * @param {Function} callback - The function to execute when timer is passed.
 * @param {Number} wait - The amount of time before debounce call is triggered.
 * @param {Boolean} immediate - Trigger the immediately.
 */
export function throttle(callback, wait, immediate = false) {
  let timeout = null
  let initialCall = true

  return function(...args) {
    const callNow = immediate && initialCall

    function next() {
      callback.apply(this, args)
      timeout = null
    }

    if (callNow) {
      initialCall = false
      next()
    }

    if (!timeout) {
      timeout = window.setTimeout(next, wait)
    }
  }
}
